import React from 'react';
import { Box, LinearProgress, Chip } from '@mui/material';
import Plot from 'react-plotly.js';
import { Strategy } from '../../../types/analytics.ts';
import GradientText from '../../../components/common/GradientText.tsx';

interface StrategyCardProps {
  strategy: Strategy;
}

const StrategyCard: React.FC<StrategyCardProps> = ({ strategy }) => {
  // Prepare sparkline data
  const dates = strategy.historicalPerformance.map(point => point.timestamp);
  const values = strategy.historicalPerformance.map(point => point.value);

  const sparklineData = [{
    x: dates,
    y: values,
    type: 'scatter',
    mode: 'lines',
    line: {
      color: strategy.isActive ? '#8a2be2' : '#666',
      width: 1,
    },
    hoverinfo: 'none',
  }];

  const sparklineLayout = {
    width: 120,
    height: 40,
    margin: { t: 0, r: 0, l: 0, b: 0 },
    paper_bgcolor: 'rgba(0,0,0,0)',
    plot_bgcolor: 'rgba(0,0,0,0)',
    xaxis: {
      visible: false,
      showgrid: false,
    },
    yaxis: {
      visible: false,
      showgrid: false,
    },
  };

  const config = {
    displayModeBar: false,
    staticPlot: true,
  };

  return (
    <Box sx={{
      background: 'rgba(26, 26, 46, 0.8)',
      backdropFilter: 'blur(10px)',
      borderRadius: 2,
      border: '1px solid rgba(138, 43, 226, 0.2)',
      p: 3,
      transition: 'transform 0.2s',
      '&:hover': {
        transform: 'translateY(-4px)',
      },
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <GradientText variant="h6">
          {strategy.name}
        </GradientText>
        <Chip
          label={strategy.isActive ? 'Active' : 'Inactive'}
          size="small"
          sx={{
            bgcolor: strategy.isActive ? 'rgba(138, 43, 226, 0.2)' : 'rgba(255, 255, 255, 0.1)',
            color: strategy.isActive ? '#8a2be2' : 'white',
          }}
        />
      </Box>

      <GradientText variant="body2" gradient="secondary" sx={{ mb: 3 }}>
        {strategy.description}
      </GradientText>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <GradientText variant="body2" gradient="accent">
            Performance
          </GradientText>
          <GradientText variant="body2">
            {strategy.performance}%
          </GradientText>
        </Box>
        <LinearProgress
          variant="determinate"
          value={strategy.performance}
          sx={{
            bgcolor: 'rgba(255, 255, 255, 0.1)',
            '& .MuiLinearProgress-bar': {
              bgcolor: '#8a2be2',
            },
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Box>
          <GradientText variant="body2" gradient="accent">
            Impact Score
          </GradientText>
          <GradientText variant="h6">
            {strategy.impactScore}
          </GradientText>
        </Box>
        <Box>
          <GradientText variant="body2" gradient="accent">
            Confidence
          </GradientText>
          <GradientText variant="h6">
            {strategy.confidenceScore}%
          </GradientText>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <GradientText variant="body2" gradient="accent">
          Trend
        </GradientText>
        <Plot
          data={sparklineData}
          layout={sparklineLayout}
          config={config}
        />
      </Box>
    </Box>
  );
};

export default StrategyCard; 